import "@/App.css";
import "@/index.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./i18n/cda";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./components/DashboardLayout";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Classrooms from "./pages/Classrooms";
import ClassroomDetail from "./pages/ClassroomDetail";
import Children from "./pages/Children";
import ChildProfile from "./pages/ChildProfile";
import Incidents from "./pages/Incidents";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Staff from "./pages/Staff";
import StaffProfile from "./pages/StaffProfile";
import StaffOnboarding from "./pages/StaffOnboarding";
import CdaCandidatesAdmin from "./pages/CdaCandidatesAdmin";
import CandidateLanding from "./pages/cda/CandidateLanding";
import CandidateJourney from "./pages/cda/CandidateJourney";
import BinderCovers from "./pages/cda/BinderCovers";
import CenterPartner from "./pages/cda/CenterPartner";
import WorkforceReadiness from "./pages/cda/WorkforceReadiness";

// SEO Pages
import CdaCareerCoachingSanAntonio from "./pages/cda/seo/CdaCareerCoachingSanAntonio";
import Cda480HoursSupport from "./pages/cda/seo/Cda480HoursSupport";
import CdaPortfolioHelp from "./pages/cda/seo/CdaPortfolioHelp";
import CdaJobReadiness from "./pages/cda/seo/CdaJobReadiness";
import LearningCenterPartnerships from "./pages/cda/seo/LearningCenterPartnerships";
import AboutDrGabrielaIbarraNoriega from "./pages/cda/seo/AboutDrGabrielaIbarraNoriega";
import CdaResources from "./pages/cda/seo/CdaResources";

// Smart front door: CDA teachers are the primary audience.
const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (user?.role === "candidate") return <Navigate to="/cda/journey" replace />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/cda" replace />;
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <div className="App">
          <BrowserRouter>
          <Routes>
            {/* Default front door → Care Comply CDA teacher experience */}
            <Route path="/" element={<RootRedirect />} />

            {/* CDA Candidate (teacher) experience */}
            <Route path="/cda" element={<CandidateLanding />} />
            <Route path="/cda/centers" element={<CenterPartner />} />
            <Route
              path="/cda/journey"
              element={
                <ProtectedRoute allowedRoles={["candidate", "admin"]} redirectTo="/cda">
                  <CandidateJourney />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cda/binder-covers"
              element={
                <ProtectedRoute allowedRoles={["candidate", "admin"]} redirectTo="/cda">
                  <BinderCovers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cda/workforce-readiness"
              element={
                <ProtectedRoute allowedRoles={["candidate", "admin"]} redirectTo="/cda">
                  <WorkforceReadiness />
                </ProtectedRoute>
              }
            />

            {/* SEO Landing Pages */}
            <Route path="/cda-career-coaching-san-antonio" element={<CdaCareerCoachingSanAntonio />} />
            <Route path="/cda-480-hours-support" element={<Cda480HoursSupport />} />
            <Route path="/cda-portfolio-help" element={<CdaPortfolioHelp />} />
            <Route path="/cda-job-readiness" element={<CdaJobReadiness />} />
            <Route path="/learning-center-partnerships" element={<LearningCenterPartnerships />} />
            <Route path="/about-dr-gabriela-ibarra-noriega" element={<AboutDrGabrielaIbarraNoriega />} />
            <Route path="/cda-resources" element={<CdaResources />} />

            {/* Director / admin (center compliance) side */}
            <Route path="/login" element={<Login />} />
            <Route
              element={
                <ProtectedRoute allowedRoles={["admin", "staff", "parent"]}>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/classrooms" element={<Classrooms />} />
              <Route path="/classrooms/:classroomId" element={<ClassroomDetail />} />
              <Route path="/children" element={<Children />} />
              <Route path="/children/:childId" element={<ChildProfile />} />
              <Route path="/staff" element={<Staff />} />
              <Route path="/staff/:staffId" element={<StaffProfile />} />
              <Route path="/staff/:staffId/onboarding" element={<StaffOnboarding />} />
              <Route path="/incidents" element={<Incidents />} />
              <Route path="/reports" element={<Reports />} />
              <Route
                path="/cda-candidates"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <CdaCandidatesAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Settings />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Anything else → CDA teacher landing */}
            <Route path="*" element={<Navigate to="/cda" replace />} />
          </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
